// API Client with automatic token management
import { tokenManager } from './tokenManager';
import { API_CONFIG } from '../config/api';

export interface ApiError {
  message: string;
  errorCode?: string;
  status: number;
  errors?: Record<string, string[]>;
  // Surfaced from the backend error body's `details.retryAfterSeconds` on a 429 (RATE_LIMIT_EXCEEDED).
  retryAfterSeconds?: number;
}

export const SENSITIVE_LOG_KEYS = ['enrollmentCode'] as const;

export function redactSensitiveForLog<T>(value: T): T {
  if (!value || typeof value !== 'object') return value;
  const obj = value as Record<string, unknown>;
  const topLevelHits = SENSITIVE_LOG_KEYS.filter((key) => Object.prototype.hasOwnProperty.call(obj, key));
  const nestedData = obj.data && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : null;
  const nestedHits = nestedData ? SENSITIVE_LOG_KEYS.filter((key) => Object.prototype.hasOwnProperty.call(nestedData, key)) : [];
  if (topLevelHits.length === 0 && nestedHits.length === 0) return value;
  const clone: Record<string, unknown> = { ...obj };
  for (const key of topLevelHits) clone[key] = '[REDACTED]';
  if (nestedHits.length > 0 && nestedData) {
    const clonedNested: Record<string, unknown> = { ...nestedData };
    for (const key of nestedHits) clonedNested[key] = '[REDACTED]';
    clone.data = clonedNested;
  }
  return clone as T;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  // Generic request method with automatic token injection
  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    console.log(`[ApiClient] ${options.method || 'GET'} ${url}${retryCount > 0 ? ` (retry ${retryCount})` : ''}`);
    
    // Get access token if available
    const accessToken = await tokenManager.getAccessToken();
    
    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add existing headers from options
    if (options.headers) {
      const optHeaders = options.headers instanceof Headers 
        ? Object.fromEntries(options.headers.entries())
        : Array.isArray(options.headers)
        ? Object.fromEntries(options.headers)
        : options.headers as Record<string, string>;
      
      Object.assign(headers, optHeaders);
    }

    // Add authorization header if token is available
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    console.log('[ApiClient] Request headers:', headers);

    // Make the request
    let response: Response;
    try {
      const fetchOptions: RequestInit = {
        ...options,
        headers,
        mode: 'cors', // Explicitly set CORS mode
        credentials: 'include', // Ensure cookies are sent
        cache: 'no-store', // Disable caching to avoid stale CORS responses
      };
      
      console.log('[ApiClient] Fetch options:', fetchOptions);
      
      response = await fetch(url, fetchOptions);
    } catch (error: any) {
      console.error('[ApiClient] Fetch error:', error);
      console.error('[ApiClient] This might be a CORS error. Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      // Re-throw as a more descriptive error
      throw new Error(`Network error: ${error.message}. This might be a CORS issue.`);
    }

    // Handle 401 errors by attempting token refresh
    if (response.status === 401 && retryCount === 0) {
      console.log('[ApiClient] Received 401, checking if we can refresh...', {
        hasRefreshToken: !!tokenManager.getRefreshToken(),
        isRefreshing: tokenManager.isRefreshing(),
        endpoint
      });

      // Don't try to refresh for auth endpoints themselves
      const isAuthEndpoint = endpoint.includes('/auth/login') ||
                            endpoint.includes('/auth/refresh') ||
                            endpoint.includes('/auth/logout');

      // Attempt refresh if we have a refresh token and not an auth endpoint
      if (!isAuthEndpoint && tokenManager.getRefreshToken()) {
        try {
          console.log('[ApiClient] Attempting to refresh token...');
          // Attempt to refresh the token (will wait if already refreshing)
          await tokenManager.refreshAccessToken();
          console.log('[ApiClient] Token refresh successful, retrying original request...');

          // Retry the request with the new token
          return this.request<T>(endpoint, options, retryCount + 1);
        } catch (refreshError) {
          console.error('[ApiClient] Token refresh failed:', refreshError);
          // Clear tokens on refresh failure to trigger logout
          tokenManager.clearTokens();
          // Continue to handle as regular 401 error
        }
      } else {
        console.log('[ApiClient] Cannot refresh:', {
          isAuthEndpoint,
          hasRefreshToken: !!tokenManager.getRefreshToken()
        });
      }
    }

    // Handle response
    if (!response.ok) {
      // Defense-in-depth: if the server returns 403 on any API call, redirect
      // to the access-denied page. The menu + <RequirePermission> wrappers
      // should already prevent most of these, but a late-arriving 403 (e.g.
      // permissions revoked mid-session) still reaches a friendly UI instead
      // of a raw error toast.
      if (response.status === 403) {
        const alreadyOnAccessDenied =
          typeof window !== 'undefined' && window.location.hash.includes('/access-denied');
        const isAuthEndpoint =
          endpoint.includes('/auth/login') ||
          endpoint.includes('/auth/refresh') ||
          endpoint.includes('/auth/logout') ||
          endpoint.includes('/auth/me');
        if (!alreadyOnAccessDenied && !isAuthEndpoint && typeof window !== 'undefined') {
          window.location.hash = '#/access-denied';
        }
      }
      await this.handleError(response);
    }

    // 204 No Content / 205 Reset Content have no body — nothing to parse.
    if (response.status === 204 || response.status === 205) {
      return undefined as unknown as T;
    }

    // Parse JSON response
    const data = await response.json();
    console.log(`[ApiClient] Response data for ${options.method || 'GET'} ${url}:`, redactSensitiveForLog(data));
    return data as T;
  }

  // Handle API errors
  private async handleError(response: Response): Promise<never> {
    let message = 'An error occurred';
    let errorCode: string | undefined;
    let errors: Record<string, string[]> | undefined;
    let retryAfterSeconds: number | undefined;

    console.error('[ApiClient] Error response:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
      url: response.url
    });

    try {
      const errorData = await response.json();
      console.log('[ApiClient] Error data:', errorData);
      message = errorData.message || errorData.error || message;
      // Support both snake_case (backend) and camelCase (legacy) error codes
      errorCode = errorData.error_code || errorData.errorCode;

      // Rate-limit retry hint lives in the nested `details` object (createBackendErrorResponse).
      if (typeof errorData.details?.retryAfterSeconds === 'number') {
        retryAfterSeconds = errorData.details.retryAfterSeconds;
      }

      // Field-level validation errors can arrive in several shapes:
      //  1. { errors: { field: ["msg"] } }                — legacy flat map
      //  2. { details: { errors: [Zod errors] } }         — sites, most handlers
      //  3. { details: { details: [Zod errors] } }        — promotions handler
      //  4. { details: [Zod errors] }                     — defensive
      const rawErrors =
        errorData.errors ??
        errorData.details?.errors ??
        errorData.details?.details ??
        (Array.isArray(errorData.details) ? errorData.details : undefined);

      if (Array.isArray(rawErrors)) {
        const grouped: Record<string, string[]> = {};
        for (const item of rawErrors) {
          const path =
            Array.isArray(item?.path) && item.path.length > 0
              ? item.path.join('.')
              : '_';
          const msg = item?.message || 'Invalid value';
          if (!grouped[path]) grouped[path] = [];
          grouped[path].push(msg);
        }
        errors = grouped;
      } else if (rawErrors && typeof rawErrors === 'object') {
        const grouped: Record<string, string[]> = {};
        for (const [k, v] of Object.entries(rawErrors)) {
          grouped[k] = Array.isArray(v) ? v.map(String) : [String(v)];
        }
        errors = grouped;
      }
    } catch (e) {
      // Failed to parse error response
      message = `HTTP ${response.status}: ${response.statusText}`;
    }

    const error: ApiError = {
      message,
      errorCode,
      status: response.status,
      errors,
      retryAfterSeconds,
    };

    console.log('[ApiClient] Throwing error:', error);
    throw error;
  }

  // Convenience methods
  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  async post<T>(endpoint: string, body?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: any, options?: RequestInit): Promise<T> {
    console.log(`[ApiClient] PATCH request to: ${endpoint}`, body);
    
    // Ensure we're not overriding important options
    const { headers: optHeaders, ...restOptions } = options || {};
    
    return this.request<T>(endpoint, {
      ...restOptions,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      headers: optHeaders,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient(API_CONFIG.baseURL);

// Type definitions for API responses
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    username: string;
    roleId: string;
    language: string;
    role?: {
      id: string;
      name: string;
      permissions: string[];
    };
  };
}

export interface UserResponse {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  roleId: string;
  language: string;
  /**
   * Vendor (merchant) ids the admin user is assigned to. Populated by the backend
   * `/auth/me` endpoint from `backend_user_vendor_assignments`. An empty array
   * means the user is unrestricted by vendor scope (super-admin pattern).
   */
  vendorIds?: string[];
  role?: {
    id: string;
    name: string;
    permissions: string[];
  };
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}